import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

interface LoginEvent {
  email: string;
  ip: string;
  device: string;
  browser: string;
  timestamp: number;
}

interface ToastItem extends LoginEvent {
  id: string;
}

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 80px;
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
`;

const Toast = styled.div<{ $isExiting?: boolean }>`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 12px;
  padding: 14px 18px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 280px;
  max-width: 360px;
  animation: ${props => props.$isExiting ? slideOut : slideIn} 0.3s ease forwards;
  pointer-events: auto;
`;

const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    width: 20px;
    height: 20px;
    color: white;
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const EmailText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
`;

const StatusText = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #10b981;
  margin-bottom: 4px;
`;

const IpText = styled.div`
  font-size: 11px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const DeviceInfo = styled.div`
  font-size: 10px;
  color: #94a3b8;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.15s ease;
  
  &:hover {
    background: #f1f5f9;
    color: #64748b;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const ToastWrapper = styled.div`
  position: relative;
`;

const LoginToast: React.FC = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [lastTimestamp, setLastTimestamp] = useState<number>(Date.now());
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());

  const removeToast = useCallback((id: string) => {
    setExitingIds(prev => new Set(prev).add(id));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      setExitingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }, 300);
  }, []);

  const fetchRecentLogins = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/recent-logins`, {
        params: { since: lastTimestamp },
        withCredentials: true
      });

      if (response.data.success && response.data.logins.length > 0) {
        const newLogins = response.data.logins.map((login: LoginEvent) => ({
          ...login,
          id: `${login.email}-${login.timestamp}`
        }));

        setToasts(prev => [...newLogins, ...prev].slice(0, 5));
        setLastTimestamp(response.data.latestTimestamp);

        // Auto-remove toasts after 6 seconds
        newLogins.forEach((toast: ToastItem) => {
          setTimeout(() => removeToast(toast.id), 6000);
        });
      }
    } catch (error) {
      // Silently fail - don't log errors for polling
    }
  }, [lastTimestamp, removeToast]);

  useEffect(() => {
    // Initial fetch
    fetchRecentLogins();

    // Poll every 3 seconds for new logins
    const interval = setInterval(fetchRecentLogins, 3000);

    return () => clearInterval(interval);
  }, [fetchRecentLogins]);

  if (toasts.length === 0) return null;

  return (
    <ToastContainer>
      {toasts.map(toast => (
        <ToastWrapper key={toast.id}>
          <Toast $isExiting={exitingIds.has(toast.id)}>
            <IconContainer>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </IconContainer>
            <ContentContainer>
              <EmailText title={toast.email}>{toast.email}</EmailText>
              <StatusText>logged in!</StatusText>
              <IpText>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
                {toast.ip}
              </IpText>
              <DeviceInfo>
                {toast.device} • {toast.browser}
              </DeviceInfo>
            </ContentContainer>
            <CloseButton onClick={() => removeToast(toast.id)}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </CloseButton>
          </Toast>
        </ToastWrapper>
      ))}
    </ToastContainer>
  );
};

export default LoginToast;
